"use client";

import * as React from "react";
import { useRouter, usePathname } from "@/lib/router/navigation";
import { Plus, Crosshair, ArrowRight, Boxes } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut,
} from "@/components/ui/command";
import { accountNavItems, projectNavSections, navItemShown } from "@/config/nav";
import type { Permission } from "@/lib/rbac/permissions";
import { useTenancy } from "@/lib/tenancy/tenancy-context";
import { useTargets } from "@/lib/hooks/use-targets";
import { useAiTestingShown } from "@/lib/hooks/use-ai-testing-available";

/** ⌘K / Ctrl+K / "/" command palette. Context-aware: account vs project scope. */
export function CommandPalette({ menu = true }: {
  /** 2.854: false where the caller cannot read the scan-profile menu (the platform layout). */
  menu?: boolean;
} = {}) {
  const router = useRouter();
  const pathname = usePathname();
  const ws = pathname.match(/^\/projects\/([^/]+)/)?.[1];
  const [open, setOpen] = React.useState(false);
  const { can, projects } = useTenancy();
  const { data: targets } = useTargets({ limit: 50 });
  // 2.854: the AI testing entries follow the served menu, fail-open.
  const aiShown = useAiTestingShown({ read: menu });

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const typing = tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable;
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || (e.key === "/" && !typing)) { e.preventDefault(); setOpen(true); }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("sentinel:command", onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("sentinel:command", onOpen); };
  }, []);

  const go = (href: string) => { setOpen(false); router.push(href); };
  const base = ws ? `/projects/${ws}` : "";

  // `perm` is typed rather than cast. It used to be `permissions.has(a.perm as never)`,
  // which meant a removed or mistyped permission compiled fine and silently hid the
  // action forever — that is how "agents:run" survived here after the permission was gone.
  interface QuickAction { label: string; href: string; icon: LucideIcon; perm: Permission }
  const allActions: QuickAction[] = ws ? [
    { label: "New scan", href: `${base}/scans/new`, icon: Plus, perm: "scans:run" },
    { label: "Add targets", href: `${base}/targets/add`, icon: Crosshair, perm: "targets:create" },
  ] : [];
  const wsActions = allActions.filter((a) => can(a.perm, ws));

  // 2.795 x 2.854: scoped to the viewed project (a grant on another project
  // must not offer this one's entries), AND the served-menu delivery gate.
  const wsNav = ws ? projectNavSections.flatMap((s) => s.items).filter((it) => navItemShown(it, (p) => can(p, ws), aiShown)) : [];

  return (
    <CommandDialog open={open} onOpenChange={setOpen} className="sm:max-w-xl">
      <CommandInput placeholder="Search or jump to…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {ws ? (
          <>
            {wsActions.length > 0 && (
              <CommandGroup heading="Actions">
                {wsActions.map((a) => <CommandItem key={a.label} onSelect={() => go(a.href)}><a.icon /> {a.label}</CommandItem>)}
              </CommandGroup>
            )}
            <CommandSeparator />
            <CommandGroup heading="This project">
              {wsNav.map((it) => (
                <CommandItem key={it.href} value={`ws ${it.title}`} onSelect={() => go(`${base}${it.href}`)}>
                  <it.icon /> {it.title}{it.badge && <CommandShortcut>{it.badge}</CommandShortcut>}
                </CommandItem>
              ))}
            </CommandGroup>
            {(targets?.items.length ?? 0) > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Targets">
                  {targets!.items.slice(0, 6).map((t) => (
                    <CommandItem key={t.id} value={`target ${t.address}`} onSelect={() => go(`${base}/targets/${t.id}`)}>
                      <ArrowRight /> <span className="font-mono text-xs">{t.address}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </>
        ) : (
          <>
            <CommandGroup heading="Account">
              {accountNavItems.filter((it) => navItemShown(it, can, aiShown)).map((it) => (
                <CommandItem key={it.href} value={`go ${it.title}`} onSelect={() => go(it.href)}><it.icon /> {it.title}</CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Open a project">
              {projects.map((w) => (
                <CommandItem key={w.id} value={`project ${w.name}`} onSelect={() => go(`/projects/${w.id}/dashboard`)}>
                  <Boxes /> {w.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
